import {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {addCalendarDays, resolvePeriod, type PeriodSelection} from '@ozon-ai/periods';
import type {ProductCatalogItem, PromotionMembershipSummary, SalesDynamics, UnitEconomics} from '@ozon-ai/ozon/contracts';
import {api} from '../../../api/client';
import {usePeriodPicker, periodSelectionKey, type PeriodPickerCommitted} from '../../../components/period-picker/use-period-picker';
import {usePeriodPresets} from '../../../components/period-picker/use-period-presets';

function previousRange(selection: PeriodSelection, anchor: string): {from: string; to: string} | null {
	try {
		const current = resolvePeriod(selection, anchor);
		let days = 1;
		let cursor = current.from;
		while (cursor < current.to && days < 400) {
			cursor = addCalendarDays(cursor, 1);
			days += 1;
		}
		const to = addCalendarDays(current.from, -1);
		return {from: addCalendarDays(to, -(days - 1)), to};
	} catch {
		return null;
	}
}

export function useProductPage(initialPeriod: PeriodPickerCommitted | null = null) {
	const {offerId: offerIdParam} = useParams();
	const offerId = offerIdParam?.trim() ?? '';
	const {presets, primaryPresetId, loading: presetsLoading} = usePeriodPresets();
	const period = usePeriodPicker({
		presets,
		primaryPresetId,
		presetsReady: !presetsLoading,
		initial: initialPeriod,
	});
	const selectionKey = periodSelectionKey(period.selection);

	const [catalog, setCatalog] = useState<ProductCatalogItem[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		void api
			.get<ProductCatalogItem[]>('/products/catalog')
			.then((items) => {
				if (!cancelled) setCatalog(items);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setCatalog(null);
					setError(err instanceof Error ? err.message : 'Не удалось загрузить каталог');
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const product = useMemo(() => {
		if (!offerId || !catalog) return null;
		return catalog.find((item) => item.offerId === offerId) ?? null;
	}, [catalog, offerId]);

	const sku = product?.ozonSku?.trim() ?? '';

	const [unitEconomics, setUnitEconomics] = useState<UnitEconomics | null>(null);
	const [unitEconomicsLoading, setUnitEconomicsLoading] = useState(false);
	const [unitEconomicsError, setUnitEconomicsError] = useState<string | null>(null);

	const [salesDynamics, setSalesDynamics] = useState<SalesDynamics | null>(null);
	const [salesDynamicsLoading, setSalesDynamicsLoading] = useState(false);
	const [salesDynamicsError, setSalesDynamicsError] = useState<string | null>(null);
	const [previousUnitEconomics, setPreviousUnitEconomics] = useState<UnitEconomics | null>(null);
	const [previousSalesDynamics, setPreviousSalesDynamics] = useState<SalesDynamics | null>(null);
	const [promotionMembership, setPromotionMembership] = useState<PromotionMembershipSummary | null>(null);
	const [previousPromotionMembership, setPreviousPromotionMembership] = useState<PromotionMembershipSummary | null>(null);

	useEffect(() => {
		if (!sku) {
			setUnitEconomics(null);
			setUnitEconomicsLoading(false);
			setUnitEconomicsError(null);
			return;
		}
		if (!period.selection) {
			setUnitEconomics(null);
			setUnitEconomicsError(period.localError);
			setUnitEconomicsLoading(false);
			return;
		}

		let cancelled = false;
		setUnitEconomicsLoading(true);
		setUnitEconomicsError(null);
		void api
			.post<UnitEconomics>('/unit-economics', {sku, period: period.selection})
			.then((next) => {
				if (!cancelled) setUnitEconomics(next);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setUnitEconomics(null);
					setUnitEconomicsError(
						err instanceof Error ? err.message : 'Не удалось посчитать юнит-экономику',
					);
				}
			})
			.finally(() => {
				if (!cancelled) setUnitEconomicsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [period.localError, period.selection, selectionKey, sku]);

	useEffect(() => {
		if (!sku) {
			setSalesDynamics(null);
			setSalesDynamicsLoading(false);
			setSalesDynamicsError(null);
			return;
		}
		if (!period.selection) {
			setSalesDynamics(null);
			setSalesDynamicsError(period.localError);
			setSalesDynamicsLoading(false);
			return;
		}

		let cancelled = false;
		setSalesDynamicsLoading(true);
		setSalesDynamicsError(null);
		void api
			.post<SalesDynamics>('/sales-dynamics', {sku, period: period.selection})
			.then((next) => {
				if (!cancelled) setSalesDynamics(next);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setSalesDynamics(null);
					setSalesDynamicsError(
						err instanceof Error ? err.message : 'Не удалось посчитать динамику продаж',
					);
				}
			})
			.finally(() => {
				if (!cancelled) setSalesDynamicsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [period.localError, period.selection, selectionKey, sku]);

	useEffect(() => {
		const previous = period.selection ? previousRange(period.selection, period.latestDay) : null;
		if (!sku || !previous) {
			setPreviousUnitEconomics(null);
			setPreviousSalesDynamics(null);
			return;
		}
		let cancelled = false;
		const previousSelection: PeriodSelection = {type: 'range', ...previous};
		void Promise.all([
			api.post<UnitEconomics>('/unit-economics', {sku, period: previousSelection}),
			api.post<SalesDynamics>('/sales-dynamics', {sku, period: previousSelection}),
		])
			.then(([economics, dynamics]) => {
				if (cancelled) return;
				setPreviousUnitEconomics(economics);
				setPreviousSalesDynamics(dynamics);
			})
			.catch(() => {
				if (cancelled) return;
				setPreviousUnitEconomics(null);
				setPreviousSalesDynamics(null);
			});
		return () => {
			cancelled = true;
		};
	}, [period.latestDay, period.selection, selectionKey, sku]);

	useEffect(() => {
		const previous = period.selection ? previousRange(period.selection, period.latestDay) : null;
		if (!sku || !period.selection) {
			setPromotionMembership(null);
			setPreviousPromotionMembership(null);
			return;
		}
		let cancelled = false;
		const previousSelection: PeriodSelection | null = previous ? {type: 'range', ...previous} : null;
		void Promise.allSettled([
			api.post<PromotionMembershipSummary>('/promotion-membership', {sku, period: period.selection}),
			previousSelection
				? api.post<PromotionMembershipSummary>('/promotion-membership', {sku, period: previousSelection})
				: Promise.resolve(null),
		]).then(([current, prior]) => {
			if (cancelled) return;
			setPromotionMembership(current.status === 'fulfilled' ? current.value : null);
			setPreviousPromotionMembership(prior.status === 'fulfilled' ? prior.value : null);
		});
		return () => {
			cancelled = true;
		};
	}, [period.latestDay, period.selection, selectionKey, sku]);

	const productNotFound = !loading && !error && catalog != null && offerId && !product;

	return {
		offerId,
		product,
		sku,
		loading,
		error,
		productNotFound,
		period,
		unitEconomics,
		unitEconomicsLoading,
		unitEconomicsError,
		salesDynamics,
		salesDynamicsLoading,
		salesDynamicsError,
		previousUnitEconomics,
		previousSalesDynamics,
		promotionMembership,
		previousPromotionMembership,
	};
}
